import { ADJUST_PANEL_TOOLS, AdjustToolDescriptor, AdjustToolId, isAdjustToolId } from './adjust.types'
import { AdjustNewBadge, AdjustNote } from './AdjustControlPrimitives.component'
import { BEIGE_300, BEIGE_600, BLUE_100, BLUE_600, GRAY_300, GRAY_400, GRAY_600, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { EditionType, getEditionTypeString, useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { FC, ReactNode } from 'react'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { AnnotateControls } from './tools/AnnotateControls.component'
import { BlurControls } from './tools/BlurControls.component'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { CropControls } from './tools/CropControls.component'
import CropOutlinedIcon from '@mui/icons-material/CropOutlined'
import { FiltersControls } from './tools/FiltersControls.component'
import { FineTuneControls } from './tools/FineTuneControls.component'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import { NonSubscriberAdjustUpsell } from '../AdjustPanel/NonSubscriberAdjustUpsell.component'
import PhotoFilterOutlinedIcon from '@mui/icons-material/PhotoFilterOutlined'
import { ResizeControls } from './tools/ResizeControls.component'
import { StickersControls } from './tools/StickersControls.component'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import ZoomOutMapOutlinedIcon from '@mui/icons-material/ZoomOutMapOutlined'
import { useAdjustEditor } from './AdjustEditor.context'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

const TOOL_ICONS: Record<AdjustToolId, ReactNode> = {
  finetune: <TuneOutlinedIcon />,
  filter: <PhotoFilterOutlinedIcon />,
  crop: <CropOutlinedIcon />,
  resize: <ZoomOutMapOutlinedIcon />,
  redact: <BlurOnIcon />,
  annotate: <BorderColorOutlinedIcon />,
  sticker: <LabelOutlinedIcon />,
}

const TOOL_CONTROLS: Record<AdjustToolId, FC<{ disabled?: boolean }>> = {
  finetune: FineTuneControls,
  filter: FiltersControls,
  crop: CropControls,
  resize: ResizeControls,
  redact: BlurControls,
  annotate: AnnotateControls,
  sticker: StickersControls,
}

/** The callout on top of the instant tools (Adjust, Blur): edits apply as they are made, with no Generate */
export const AdjustInstructions: FC<{ title?: string, description?: string }> = ({ title, description }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  return (
    <BorderBoxWrapper gap={0.2} padding={1.2} borderColor={BEIGE_600} backgroundColor={BEIGE_300} sx={{ borderRadius: '0.8rem', width: '100%', boxSizing: 'border-box' }}>

      <Stack gap={0.2}>

        <Typography variant="text-sm" fontWeight={550} color={GRAY_900} sx={{ fontSize: '1.4rem', lineHeight: 1.4, letterSpacing: '-0.56px' }}>
          {title ?? t('adjust_instructions.title')}
        </Typography>

        <Typography variant="text-sm" fontWeight={350} color={GRAY_900} sx={{ fontSize: '1.4rem', lineHeight: 1.4, letterSpacing: '-0.56px' }}>
          {description ?? t('adjust.instructions')}
        </Typography>

      </Stack>

    </BorderBoxWrapper>
  )
}

interface AdjustToolRowProps {
  tool: AdjustToolDescriptor
  isOpen: boolean
  disabled: boolean
  onToggle: () => void
  /** The open tool's controls, under its header */
  children?: ReactNode
}

/** One tool of the list: its header (icon, name, chevron) opens it, its controls show under it while open */
export const AdjustToolRow: FC<AdjustToolRowProps> = ({ tool, isOpen, disabled, onToggle, children }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  return (
    <Box sx={{ borderRadius: '0.8rem', border: `1px solid ${isOpen ? BLUE_600 : GRAY_400}`, backgroundColor: WHITE, flexShrink: 0 }}>

      <Box
        component="button"
        type="button"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={onToggle}
        sx={{
          font: 'inherit',
          width: '100%',
          height: '4.4rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          padding: '0 1.2rem',
          border: 0,
          borderRadius: isOpen ? '0.8rem 0.8rem 0 0' : '0.8rem',
          backgroundColor: isOpen ? BLUE_100 : 'transparent',
          color: isOpen ? GRAY_900 : GRAY_700,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          textAlign: 'left',
          '&:hover': { backgroundColor: disabled ? undefined : BLUE_100, color: disabled ? undefined : GRAY_900 },
          '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: 2 },
          '& .MuiSvgIcon-root': { fontSize: '2rem' },
        }}
      >

        {TOOL_ICONS[tool.id]}

        <Typography variant="text-sm" sx={{ fontSize: '1.4rem', fontWeight: isOpen ? 550 : 450, color: 'inherit' }}>{t(tool.labelKey)}</Typography>

        {tool.isNew && <AdjustNewBadge />}

        <KeyboardArrowDownRoundedIcon sx={{ marginLeft: 'auto', color: GRAY_600, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }} />

      </Box>

      {isOpen && children && (
        <Box sx={{ padding: '1.2rem', borderTop: `1px solid ${GRAY_300}` }}>
          {children}
        </Box>
      )}

    </Box>
  )
}

interface AdjustToolsPanelProps {
  /** Inside the Adjust section of Combine tools: no own padding or scroll (the list scrolls) */
  embedded?: boolean
  /** Combine tools: picking a tool first puts the editor on the canvas */
  beforeToolSelect?: () => Promise<void>
}

/**
 * Adjust panel of the in-house editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT): the 7 tools as one list, the
 * open tool's controls inline under it — the photo never moves, in the One tool column (320 px) as in the Combine tools
 * list. Same props as the Pintura AdjustPanel it replaces.
 */
export const AdjustToolsPanel: FC<AdjustToolsPanelProps> = ({ embedded = false, beforeToolSelect }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const {
    isAdjustTool,
    selectedPinturaFeature,
    setSelectedPinturaFeature,
    isProcessingImage,
    isOriginalVisualType,
    logPhotoStudioEvent,
    getCommonAnalyticsData,
    sourceVisualFilename,
  } = useClientPhotoStudio()
  const { base, isBaseError, tool: editorTool, setToolOpen } = useAdjustEditor()

  const isDisabled = isProcessingImage || isOriginalVisualType
  // One section open at most; the open one can be collapsed too (every section closed)
  const openTool = isAdjustTool && isAdjustToolId(selectedPinturaFeature) ? editorTool : null

  const toggleTool = async (tool: AdjustToolId) => {
    if (isDisabled) return
    if (openTool === tool) {
      setToolOpen(false)
      return
    }
    await beforeToolSelect?.()
    setSelectedPinturaFeature(tool)
    setToolOpen(true)
    const event = { editionType: getEditionTypeString(EditionType.ADJUST, tool), editionSettings: { pinturaFeature: tool } }
    getCommonAnalyticsData()
      .then(commonData => logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, { ...event, ...commonData }))
      .catch(() => logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, { ...event, sourceVisualFilename }))
  }

  return (
    <>

      {!hasWorkspaceActiveSubscription && <NonSubscriberAdjustUpsell />}

      {hasWorkspaceActiveSubscription && (
        <Stack
          gap="0.8rem"
          sx={{
            width: '100%',
            // The column adds none: the scroll container's edge, where it clips, stays clear of a slider thumb at its minimum
            padding: embedded ? 0 : '2.4rem',
            boxSizing: 'border-box',
            ...(embedded ? {} : { height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }),
          }}
        >

          <AdjustInstructions />

          {isBaseError && <AdjustNote>{t('adjust.load_error')}</AdjustNote>}

          {/* Blur is a tool of its own (BlurPanel) */}
          {ADJUST_PANEL_TOOLS.map((tool) => {
            const Controls = TOOL_CONTROLS[tool.id]
            return (
              <AdjustToolRow key={tool.id} tool={tool} isOpen={openTool === tool.id} disabled={isDisabled} onToggle={() => toggleTool(tool.id)}>
                <Controls disabled={isDisabled || !base} />
              </AdjustToolRow>
            )
          })}

        </Stack>
      )}

    </>
  )
}
